import {
  collection,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
  type DocumentData,
  type QueryConstraint,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { firestore, functions } from '../firebase';
import { RepairRequest, RepairStatus, ServiceProvider } from '../types/firestore';
import {
  formatRepairRequestFulfillmentAddress,
  getRepairRequestFulfillmentPaymentLabel,
  resolveRepairRequestFulfillmentSnapshot,
} from '../shared/fulfillment';
import {
  REPAIR_STATUS_ORDER,
  getRepairRequestStatusLabel,
  isTerminalRepairRequestStatus,
  requiresClientAction,
  requiresServiceAction,
} from '../utils/repairRequestWorkflow';

export const ADMIN_REQUESTS_PAGE_SIZE = 80;
export const ADMIN_REQUESTS_RISK_SCAN_LIMIT = 300;

export type AdminRequestCursor = QueryDocumentSnapshot<DocumentData> | null;
export type AdminRequestBucket =
  | 'active'
  | 'requiresClientAction'
  | 'requiresServiceAction'
  | 'terminal'
  | 'problematic';
export type AdminRequestRiskFlag =
  | 'missingFulfillmentSnapshot'
  | 'missingManualDeviceType'
  | 'missingBrandOrModel'
  | 'unknownStatus'
  | 'legacyV1'
  | 'missingServiceProviderId'
  | 'missingClientId';
export type AdminRequestArchiveView = 'active' | 'archived' | 'all';
export type AdminRequestArchiveReasonCode =
  | 'test_cleanup'
  | 'orphaned_service'
  | 'broken_legacy'
  | 'duplicate'
  | 'other';

export interface AdminRequestsMetric {
  value: number | null;
  label: string;
  description: string;
  partial?: boolean;
}

export interface AdminRequestsMetrics {
  total: AdminRequestsMetric;
  active: AdminRequestsMetric;
  new: AdminRequestsMetric;
  requiresClientAction: AdminRequestsMetric;
  requiresServiceAction: AdminRequestsMetric;
  inProgress: AdminRequestsMetric;
  terminal: AdminRequestsMetric;
  problematic: AdminRequestsMetric;
}

export interface AdminRequestRuntimeClassification {
  bucket: AdminRequestBucket;
  status: RepairStatus | string | null;
  statusLabel: string;
  isKnownStatus: boolean;
  isActive: boolean;
  isTerminal: boolean;
  isLegacy: boolean;
  isProblematic: boolean;
}

export interface AdminRequestDirectoryItem {
  id: string;
  requestIndex: string | null;
  serviceOperationalIndex: string | null;
  status: RepairStatus | string | null;
  statusLabel: string;
  bucket: AdminRequestBucket;
  bucketLabel: string;
  displayTitle: string;
  serviceProviderId: string | null;
  serviceProviderName: string;
  clientId: string | null;
  requesterLabel: string;
  createdAt: Date | null;
  updatedAt: Date | null;
  createdAtLabel: string;
  updatedAtLabel: string;
  fulfillmentMode: string | null;
  fulfillmentSummary: string;
  activitySummary: string;
  workflowVersionLabel: string;
  isAdminArchived: boolean;
  adminArchiveReasonCode: AdminRequestArchiveReasonCode | null;
  adminArchivedAt: Date | null;
  adminArchivedAtLabel: string;
  riskFlags: AdminRequestRiskFlag[];
  riskLabels: string[];
  searchText: string;
  rawRequest: Partial<RepairRequest> & Record<string, unknown>;
}

export interface AdminRequestsReadModel {
  metrics: AdminRequestsMetrics;
  requests: AdminRequestDirectoryItem[];
  cursor: AdminRequestCursor;
  hasMore: boolean;
  isPartial: boolean;
  partialError: string | null;
  readAt: Date;
  readLimits: {
    pageSize: number;
    riskScanLimit: number;
  };
}

const metric = (
  value: number | null,
  label: string,
  description: string,
  partial = false
): AdminRequestsMetric => ({
  value,
  label,
  description,
  partial,
});

const emptyMetrics = (): AdminRequestsMetrics => ({
  total: metric(null, 'Wszystkie', 'Liczba dokumentów repairRequests', true),
  active: metric(null, 'Aktywne', 'Statusy nieterminalne według repairRequestWorkflow', true),
  new: metric(null, 'Nowe', "repairRequests.status == 'new'", true),
  requiresClientAction: metric(null, 'Reakcja klienta', 'Statusy wymagające decyzji klienta', true),
  requiresServiceAction: metric(
    null,
    'Reakcja serwisu',
    'Statusy wymagające działania serwisu',
    true
  ),
  inProgress: metric(null, 'W toku', 'Aktywna realizacja po decyzjach workflow', true),
  terminal: metric(null, 'Terminalne', 'Zakończone, odrzucone lub anulowane', true),
  problematic: metric(null, 'Legacy/problemy', 'Nieznane statusy lub ryzyka danych', true),
});

const trimString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
};

const normalizeTimestamp = (value: unknown): Date | null => {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  if (typeof (value as { toDate?: unknown }).toDate === 'function') {
    const date = (value as { toDate: () => Date }).toDate();
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (
    typeof (value as { seconds?: unknown }).seconds === 'number' &&
    Number.isFinite((value as { seconds: number }).seconds)
  ) {
    return new Date((value as { seconds: number }).seconds * 1000);
  }
  return null;
};

const formatDateTime = (date: Date | null): string =>
  date
    ? new Intl.DateTimeFormat('pl-PL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date)
    : 'Brak daty';

const countCollection = async (
  collectionName: string,
  ...constraints: QueryConstraint[]
): Promise<number> => {
  const snapshot = await getCountFromServer(
    query(collection(firestore, collectionName), ...constraints)
  );
  return snapshot.data().count;
};

const isKnownRepairStatus = (status: unknown): status is RepairStatus =>
  typeof status === 'string' && (Object.values(RepairStatus) as string[]).includes(status);

export const classifyAdminRequestRuntime = (
  request: Partial<RepairRequest> & Record<string, unknown>
): AdminRequestRuntimeClassification => {
  const status = typeof request.status === 'string' ? request.status : null;
  const isKnownStatus = isKnownRepairStatus(status);
  const isLegacy = request.workflowVersion !== 2;

  if (!isKnownStatus) {
    return {
      bucket: 'problematic',
      status,
      statusLabel: status ? `Nieznany status: ${status}` : 'Brak statusu',
      isKnownStatus: false,
      isActive: false,
      isTerminal: false,
      isLegacy,
      isProblematic: true,
    };
  }

  const isTerminal = isTerminalRepairRequestStatus(status);
  const bucket: AdminRequestBucket = isTerminal
    ? 'terminal'
    : requiresClientAction(status)
      ? 'requiresClientAction'
      : requiresServiceAction(status)
        ? 'requiresServiceAction'
        : 'active';

  return {
    bucket,
    status,
    statusLabel: getRepairRequestStatusLabel(status),
    isKnownStatus: true,
    isActive: !isTerminal,
    isTerminal,
    isLegacy,
    isProblematic: false,
  };
};

export const getRequestRiskFlags = (
  request: Partial<RepairRequest> & Record<string, unknown>
): AdminRequestRiskFlag[] => {
  const classification = classifyAdminRequestRuntime(request);
  const flags: Array<AdminRequestRiskFlag | null> = [
    request.workflowVersion === 2 && !request.fulfillmentSnapshot
      ? 'missingFulfillmentSnapshot'
      : null,
    trimString(request.manualDeviceType) ? null : 'missingManualDeviceType',
    trimString(request.brand) && trimString(request.model) ? null : 'missingBrandOrModel',
    classification.isKnownStatus ? null : 'unknownStatus',
    classification.isLegacy ? 'legacyV1' : null,
    trimString(request.serviceProviderId) ? null : 'missingServiceProviderId',
    trimString(request.clientId) ? null : 'missingClientId',
  ];

  return flags.filter(Boolean) as AdminRequestRiskFlag[];
};

export const getAdminRequestRiskLabel = (flag: AdminRequestRiskFlag): string => {
  const labels: Record<AdminRequestRiskFlag, string> = {
    missingFulfillmentSnapshot: 'Brak fulfillmentSnapshot',
    missingManualDeviceType: 'Brak typu sprzętu',
    missingBrandOrModel: 'Brak marki/modelu',
    unknownStatus: 'Nieznany status',
    legacyV1: 'Legacy V1',
    missingServiceProviderId: 'Brak serviceProviderId',
    missingClientId: 'Brak clientId',
  };
  return labels[flag];
};

export const resolveRequestDisplayTitle = (
  request: Partial<RepairRequest> & Record<string, unknown>
): string => {
  const manualDeviceType = trimString(request.manualDeviceType);
  if (manualDeviceType) {
    return [manualDeviceType, trimString(request.brand), trimString(request.model)]
      .filter(Boolean)
      .join(' ');
  }

  const subjectSnapshot = request.subjectSnapshot;
  if (subjectSnapshot && typeof subjectSnapshot === 'object') {
    const subjectName = trimString((subjectSnapshot as { name?: unknown }).name);
    if (subjectName) return subjectName;
  }

  const legacyEquipment = [
    trimString(request.equipmentTypeNameSnapshot),
    trimString(request.deviceType),
    trimString(request.brand),
    trimString(request.model),
  ]
    .filter(Boolean)
    .join(' ');

  return legacyEquipment || 'Zgłoszenie bez opisu urządzenia';
};

export const getAdminRequestBucketLabel = (bucket: AdminRequestBucket): string => {
  const labels: Record<AdminRequestBucket, string> = {
    active: 'Aktywne',
    requiresClientAction: 'Reakcja klienta',
    requiresServiceAction: 'Reakcja serwisu',
    terminal: 'Terminalne',
    problematic: 'Legacy/problem',
  };
  return labels[bucket];
};

const getRequesterLabel = (request: Partial<RepairRequest> & Record<string, unknown>): string => {
  const clientDetails = request.clientDetails;
  if (clientDetails && typeof clientDetails === 'object') {
    const name = trimString((clientDetails as { displayName?: unknown }).displayName);
    const email = trimString((clientDetails as { email?: unknown }).email);
    if (name && email) return `${name} (${email})`;
    if (name) return name;
    if (email) return email;
  }
  return trimString(request.clientId) || 'Brak klienta';
};

const getFulfillmentSummary = (request: Partial<RepairRequest>): string => {
  const resolved = resolveRepairRequestFulfillmentSnapshot(request);
  if (!resolved.selectedMode) {
    return resolved.state === 'missing' ? 'Brak fulfillmentSnapshot' : 'Niekompletny snapshot';
  }

  const parts = [
    resolved.selectedModeLabel,
    formatRepairRequestFulfillmentAddress(resolved.address),
    getRepairRequestFulfillmentPaymentLabel(resolved.payment),
  ].filter(Boolean);

  return parts.join(' · ');
};

const getActivitySummary = (request: Partial<RepairRequest>): string => {
  const parts = [
    Array.isArray(request.serviceUnreadSections) && request.serviceUnreadSections.length > 0
      ? `Serwis: ${request.serviceUnreadSections.join(', ')}`
      : null,
    Array.isArray(request.clientUnreadSections) && request.clientUnreadSections.length > 0
      ? `Klient: ${request.clientUnreadSections.join(', ')}`
      : null,
    trimString(request.serviceUnreadActivityType)
      ? `Aktywność serwisu: ${trimString(request.serviceUnreadActivityType)}`
      : null,
    trimString(request.clientUnreadActivityType)
      ? `Aktywność klienta: ${trimString(request.clientUnreadActivityType)}`
      : null,
  ].filter(Boolean);

  return parts.length ? parts.join(' · ') : 'Brak sygnału unread/activity';
};

const getServiceProviderIds = (requests: Array<Partial<RepairRequest>>): string[] =>
  Array.from(
    new Set(
      requests
        .map((request) => trimString(request.serviceProviderId))
        .filter((value): value is string => Boolean(value))
    )
  );

const loadServiceProviderNames = async (
  requests: Array<Partial<RepairRequest>>
): Promise<Record<string, string>> => {
  const ids = getServiceProviderIds(requests);
  const entries = await Promise.all(
    ids.map(async (id) => {
      const snapshot = await getDoc(doc(firestore, 'serviceProviders', id));
      if (!snapshot.exists()) return [id, id] as const;
      const provider = snapshot.data() as Partial<ServiceProvider>;
      return [id, trimString(provider.serviceName) || id] as const;
    })
  );
  return Object.fromEntries(entries);
};

export const requestDocToDirectoryItem = (
  id: string,
  data: Record<string, unknown>,
  serviceProviderNames: Record<string, string> = {}
): AdminRequestDirectoryItem => {
  const request = { id, uid: id, ...data } as Partial<RepairRequest> & Record<string, unknown>;
  const classification = classifyAdminRequestRuntime(request);
  const riskFlags = getRequestRiskFlags(request);
  const providerId = trimString(request.serviceProviderId);
  const createdAt = normalizeTimestamp(request.createdAt);
  const updatedAt = normalizeTimestamp(request.updatedAt);
  const fulfillment = resolveRepairRequestFulfillmentSnapshot(request);
  const requestIndex = trimString(request.requestIndex);
  const serviceOperationalIndex = trimString(request.serviceOperationalIndex);
  const displayTitle = resolveRequestDisplayTitle(request);
  const adminArchivedAt = normalizeTimestamp(request.adminArchivedAt);
  const serviceProviderName = providerId
    ? serviceProviderNames[providerId] || providerId
    : 'Brak serwisu';
  const requesterLabel = getRequesterLabel(request);
  const searchText = [
    id,
    requestIndex,
    serviceOperationalIndex,
    displayTitle,
    serviceProviderName,
    requesterLabel,
    request.status,
    request.workflowVersion,
    fulfillment.selectedMode,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return {
    id,
    requestIndex,
    serviceOperationalIndex,
    status: classification.status,
    statusLabel: classification.statusLabel,
    bucket: classification.bucket,
    bucketLabel: getAdminRequestBucketLabel(classification.bucket),
    displayTitle,
    serviceProviderId: providerId,
    serviceProviderName,
    clientId: trimString(request.clientId),
    requesterLabel,
    createdAt,
    updatedAt,
    createdAtLabel: formatDateTime(createdAt),
    updatedAtLabel: formatDateTime(updatedAt),
    fulfillmentMode: fulfillment.selectedMode || null,
    fulfillmentSummary: getFulfillmentSummary(request),
    activitySummary: getActivitySummary(request),
    workflowVersionLabel: request.workflowVersion === 2 ? 'V2' : 'Legacy V1',
    isAdminArchived: Boolean(adminArchivedAt),
    adminArchiveReasonCode:
      typeof request.adminArchiveReasonCode === 'string'
        ? (request.adminArchiveReasonCode as AdminRequestArchiveReasonCode)
        : null,
    adminArchivedAt,
    adminArchivedAtLabel: formatDateTime(adminArchivedAt),
    riskFlags,
    riskLabels: riskFlags.map(getAdminRequestRiskLabel),
    searchText,
    rawRequest: request,
  };
};

const loadRequestMetrics = async (): Promise<AdminRequestsMetrics> => {
  const [total, statusCounts, riskScanSnapshot] = await Promise.all([
    countCollection('repairRequests'),
    Promise.all(
      REPAIR_STATUS_ORDER.map(async (status) => ({
        status,
        count: await countCollection('repairRequests', where('status', '==', status)),
      }))
    ),
    getDocs(
      query(
        collection(firestore, 'repairRequests'),
        orderBy('updatedAt', 'desc'),
        limit(ADMIN_REQUESTS_RISK_SCAN_LIMIT)
      )
    ),
  ]);

  const countByStatus = statusCounts.reduce(
    (acc, { status, count }) => ({ ...acc, [status]: count }),
    {} as Record<RepairStatus, number>
  );
  const active = statusCounts
    .filter(({ status }) => !isTerminalRepairRequestStatus(status))
    .reduce((sum, { count }) => sum + count, 0);
  const terminal = statusCounts
    .filter(({ status }) => isTerminalRepairRequestStatus(status))
    .reduce((sum, { count }) => sum + count, 0);
  const clientAction = statusCounts
    .filter(({ status }) => requiresClientAction(status))
    .reduce((sum, { count }) => sum + count, 0);
  const serviceAction = statusCounts
    .filter(({ status }) => requiresServiceAction(status))
    .reduce((sum, { count }) => sum + count, 0);
  const inProgress = [
    RepairStatus.ACCEPTED,
    RepairStatus.AWAITING_FULFILLMENT,
    RepairStatus.DIAGNOSIS_IN_PROGRESS,
    RepairStatus.IN_PROGRESS,
    RepairStatus.PENDING_PAYMENT,
  ].reduce((sum, status) => sum + (countByStatus[status] || 0), 0);
  const known = statusCounts.reduce((sum, { count }) => sum + count, 0);
  const problematicByUnknownStatus = Math.max(total - known, 0);
  const problematicInRiskScan = riskScanSnapshot.docs.filter(
    (docSnap) => getRequestRiskFlags(docSnap.data() as Partial<RepairRequest>).length > 0
  ).length;
  const riskScanPartial = riskScanSnapshot.size >= ADMIN_REQUESTS_RISK_SCAN_LIMIT;

  return {
    total: metric(total, 'Wszystkie', 'Liczba dokumentów repairRequests'),
    active: metric(active, 'Aktywne', 'Statusy nieterminalne według repairRequestWorkflow'),
    new: metric(countByStatus[RepairStatus.NEW] || 0, 'Nowe', "repairRequests.status == 'new'"),
    requiresClientAction: metric(clientAction, 'Reakcja klienta', 'Statusy wymagające klienta'),
    requiresServiceAction: metric(serviceAction, 'Reakcja serwisu', 'Statusy wymagające serwisu'),
    inProgress: metric(
      inProgress,
      'W toku',
      'accepted/awaitingFulfillment/diagnosisInProgress/inProgress/payment'
    ),
    terminal: metric(terminal, 'Terminalne', 'Zakończone, odrzucone i anulowane'),
    problematic: metric(
      Math.max(problematicByUnknownStatus, problematicInRiskScan),
      'Legacy/problemy',
      `Ryzyka danych w limicie ${ADMIN_REQUESTS_RISK_SCAN_LIMIT} + nieznane statusy`,
      riskScanPartial
    ),
  };
};

const loadRequestPage = async (
  pageSize: number,
  cursor: AdminRequestCursor
): Promise<{
  requests: AdminRequestDirectoryItem[];
  cursor: AdminRequestCursor;
  hasMore: boolean;
}> => {
  const constraints: QueryConstraint[] = [orderBy('updatedAt', 'desc'), limit(pageSize)];
  if (cursor) {
    constraints.splice(1, 0, startAfter(cursor));
  }

  const snapshot = await getDocs(query(collection(firestore, 'repairRequests'), ...constraints));
  const rawRequests = snapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    data: docSnap.data(),
  }));
  const serviceProviderNames = await loadServiceProviderNames(
    rawRequests.map((entry) => entry.data as Partial<RepairRequest>)
  );

  return {
    requests: rawRequests.map((entry) =>
      requestDocToDirectoryItem(entry.id, entry.data, serviceProviderNames)
    ),
    cursor: snapshot.docs[snapshot.docs.length - 1] || null,
    hasMore: snapshot.size >= pageSize,
  };
};

export const getAdminRequestsReadModel = async (
  pageSize = ADMIN_REQUESTS_PAGE_SIZE,
  cursor: AdminRequestCursor = null
): Promise<AdminRequestsReadModel> => {
  const [metricsResult, pageResult] = await Promise.allSettled([
    loadRequestMetrics(),
    loadRequestPage(pageSize, cursor),
  ]);

  if (pageResult.status === 'rejected') {
    throw pageResult.reason;
  }

  const metrics = metricsResult.status === 'fulfilled' ? metricsResult.value : emptyMetrics();
  const partialError =
    metricsResult.status === 'rejected'
      ? 'Nie udało się odczytać części metryk. Lista zgłoszeń jest dostępna.'
      : null;

  return {
    metrics,
    requests: pageResult.value.requests,
    cursor: pageResult.value.cursor,
    hasMore: pageResult.value.hasMore,
    isPartial: Boolean(partialError) || Object.values(metrics).some((entry) => entry.partial),
    partialError,
    readAt: new Date(),
    readLimits: {
      pageSize,
      riskScanLimit: ADMIN_REQUESTS_RISK_SCAN_LIMIT,
    },
  };
};

export const filterRequestsByArchiveView = (
  requests: AdminRequestDirectoryItem[],
  archiveView: AdminRequestArchiveView
): AdminRequestDirectoryItem[] => {
  if (archiveView === 'all') return requests;
  if (archiveView === 'archived') return requests.filter((request) => request.isAdminArchived);
  return requests.filter((request) => !request.isAdminArchived);
};

interface AdminArchiveRepairRequestInput {
  requestId: string;
  reasonCode: AdminRequestArchiveReasonCode;
  note?: string;
}

interface AdminArchiveRepairRequestOutput {
  success: true;
  requestId: string;
  result: 'archived' | 'idempotent';
  reasonCode: AdminRequestArchiveReasonCode;
}

interface AdminRestoreRepairRequestArchiveInput {
  requestId: string;
  note?: string;
}

interface AdminRestoreRepairRequestArchiveOutput {
  success: true;
  requestId: string;
  result: 'restored' | 'idempotent';
}

interface AdminArchiveRepairRequestsBatchInput {
  requestIds: string[];
  reasonCode: AdminRequestArchiveReasonCode;
  note?: string;
}

export interface AdminArchiveRepairRequestsBatchOutput {
  success: true;
  requestedCount: number;
  archivedCount: number;
  idempotentCount: number;
  notFoundCount: number;
  reasonCode: AdminRequestArchiveReasonCode;
  results: Array<{
    requestId: string;
    result: 'archived' | 'idempotent' | 'not-found';
  }>;
}

export const archiveAdminRequest = async (
  payload: AdminArchiveRepairRequestInput
): Promise<AdminArchiveRepairRequestOutput> => {
  const callable = httpsCallable<AdminArchiveRepairRequestInput, AdminArchiveRepairRequestOutput>(
    functions,
    'adminArchiveRepairRequest'
  );
  const result = await callable(payload);
  return result.data;
};

export const restoreAdminRequestArchive = async (
  payload: AdminRestoreRepairRequestArchiveInput
): Promise<AdminRestoreRepairRequestArchiveOutput> => {
  const callable = httpsCallable<
    AdminRestoreRepairRequestArchiveInput,
    AdminRestoreRepairRequestArchiveOutput
  >(functions, 'adminRestoreRepairRequestArchive');
  const result = await callable(payload);
  return result.data;
};

export const archiveAdminRequestsBatch = async (
  payload: AdminArchiveRepairRequestsBatchInput
): Promise<AdminArchiveRepairRequestsBatchOutput> => {
  const callable = httpsCallable<
    AdminArchiveRepairRequestsBatchInput,
    AdminArchiveRepairRequestsBatchOutput
  >(functions, 'adminArchiveRepairRequestsBatch');
  const result = await callable(payload);
  return result.data;
};
